import React, { useEffect, useState } from "react";

import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";

import store, { persistor } from "store/store";
import { PersistGate } from "redux-persist/integration/react";

import {
  AuthProvider,
  UpdateAppProvider,
  OnBoardingProvider,
  ThemeProvider,
} from "providers";

import { QueryClientProvider, QueryClient } from "@tanstack/react-query";

import { SkeletonTheme } from "react-loading-skeleton";
import { ToastContainer, toast } from "react-toastify";

import Routes from "./Routes";

import { setUpInterceptorStore } from "utils/Api";
import { SplashScreen, UpdateAppModal } from "components";

import useServiceWorker from "./hooks/useServiceWorker";
import PageTitleHandler from "providers/PageTitleHandler";
import ImpersonateHandler from "providers/ImpersonateHandler";
import InfoAppModal from "components/InfoAppModal";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

const App = () => {
  const { waitingWorker, showReload, reloadPage } = useServiceWorker();

  const [showUpdateModal, setShowUpateModal] = useState(false);

  useEffect(() => {
    if (showReload && waitingWorker) {
      setShowUpateModal(true);
    }
  }, [waitingWorker, showReload, reloadPage]);

  return (
    <Provider store={store}>
      <ThemeProvider>
        <UpdateAppModal
          isOpen={showUpdateModal}
          setIsOpen={setShowUpateModal}
          onUpdate={() => reloadPage()}
        />
        {/* Redux Persistor Gate */}
        <PersistGate persistor={persistor} loading={<SplashScreen />}>
          <BrowserRouter>
            {/* Skeleton Loading Theme Provider */}
            <SkeletonTheme borderRadius={8}>
              {/* Toast Messages Container */}
              <ToastContainer
                position="top-center"
                rtl={true}
                autoClose={3000}
              />
              {/* Query Client Provider */}
              <QueryClientProvider client={queryClient}>
                {/* Authentication Logic Provider */}
                <AuthProvider>
                  {/* Native App Onboaring Logic Provider */}
                  <OnBoardingProvider>
                    <ImpersonateHandler />
                    <PageTitleHandler />
                    <Routes />
                  </OnBoardingProvider>
                </AuthProvider>
              </QueryClientProvider>
            </SkeletonTheme>
          </BrowserRouter>
        </PersistGate>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
